import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  output,
  signal,
} from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  ModalComponent as DpacModalComponent,
  InputFieldComponent,
  ButtonComponent,
  SelectComponent,
} from '@mic-ac/ui-kit';

import { AuthServiceService } from '../../services/auth-service/auth-service.service';
import { FormOptionsService } from '../../services/wizard-form-data/wizard-page-data';
import { ApiEnteProprietario } from '../../../shared/models/api-collection.models';
import { IndirizzoSuggestion, ProprietarioCollezione } from '../../../shared/models/wizardData';

@Component({
  selector: 'app-nuovo-ente-modal',
  standalone: true,
  imports: [
    DpacModalComponent,
    InputFieldComponent,
    ButtonComponent,
    SelectComponent,
    ReactiveFormsModule,
  ],
  templateUrl: './nuovo-ente-modal.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NuovoEnteModalComponent {
  readonly #fb = inject(FormBuilder);
  readonly #authService = inject(AuthServiceService);
  readonly #opts = inject(FormOptionsService);

  closed = output<ApiEnteProprietario | null>();
  submitting = signal(false);

  tipologie = this.#opts.optionsSignal('proprietario');
  categorie = this.#opts.optionsSignal('categoriaproprietario');
  addressSuggestions = signal<IndirizzoSuggestion[]>([]);

  isFisico = signal(false);
  nomeLabel = computed(() => (this.isFisico() ? 'Nome' : 'Denominazone'));
  lastFieldFullWidth = computed(() => this.isFisico());

  form: FormGroup = this.#fb.group({
    tipologia: [null, Validators.required],
    cognome: [''],
    nome: ['', Validators.required],
    categoria: [null, Validators.required],
    codiceIdentificativo: [''],
    indirizzo: ['', Validators.required],
    indirizzoId: [null],
    civico: ['', Validators.required],
    cap: ['', [Validators.required, Validators.pattern(/^\d{5}$/)]],
    comune: ['', Validators.required],
    provincia: ['', Validators.required],
    sitoWeb: ['', Validators.pattern(/^https?:\/\/.+/i)],
  });

  constructor() {
    this.form.get('tipologia')?.valueChanges.subscribe(v => {
      const fisico = v === 'PR004';
      this.applyCognomeValidator(fisico);
      this.isFisico.set(fisico);
    });
  }

  private applyCognomeValidator(required: boolean): void {
    const ctrl = this.form.get('cognome');
    if (!ctrl) return;
    ctrl.setValidators(required ? [Validators.required] : []);
    ctrl.updateValueAndValidity({ emitEvent: false });
  }

  searchAddress(term: string): void {
    if (!term || term.length < 3) {
      this.addressSuggestions.set([]);
      return;
    }
    this.#authService.indirizzoSuggestion(term).subscribe({
      next: list => this.addressSuggestions.set(list),
      error: () => this.addressSuggestions.set([]),
    });
  }

  onAddressSelect(selection: IndirizzoSuggestion | string): void {
    if (!selection || typeof selection === 'string') return;
    this.form.patchValue({
      indirizzo: selection.indirizzoEsteso ?? selection.indirizzo ?? '',
      civico: selection.civico ?? '',
      cap: selection.cap ?? '',
      comune: selection.comune ?? '',
      provincia: selection.provincia ?? '',
      indirizzoId: (selection as any).id ?? null,
    });
  }

  private buildPayload(): ProprietarioCollezione {
    const v = this.form.value;
    const isFisico = v.tipologia === 'PR004';
    const rawTip = (opts: { value: string; raw?: unknown }[], code: string | null) =>
      !code ? null : (opts.find(o => o.value === code)?.raw ?? { codice: code });
    return {
      nome: isFisico ? v.nome : null,
      cognome: isFisico ? v.cognome : null,
      nominativo: isFisico ? null : v.nome,
      tipologia: rawTip(this.tipologie(), v.tipologia) as any,
      categorie: [rawTip(this.categorie(), v.categoria) as any],
      codiceIdentificativo: v.codiceIdentificativo || null,
      indirizzo: {
        ...(v.indirizzoId ? { id: v.indirizzoId } : {}),
        indirizzo: v.indirizzo || null,
        civico: v.civico || null,
        cap: v.cap || null,
        comune: v.comune || null,
        provincia: v.provincia || null,
      } as any,
      sitoWeb: v.sitoWeb || null,
    };
  }

  onSubmit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    this.submitting.set(true);
    this.#authService.setupProprietario(this.buildPayload()).subscribe({
      next: res => {
        this.submitting.set(false);
        this.closed.emit(res.element);
      },
      error: () => {
        this.submitting.set(false);
      },
    });
  }

  dismiss(): void {
    this.closed.emit(null);
  }
}
